import type {AxiosResponse} from "axios";

import type {GetListResponse} from "@/src/modules/core/types";

import {mergeAndSortThreads, sliceConversationPage} from "../lib/merge-threads";
import {directMessagesApi} from "../api";
import type {
  DirectMessageBox,
  DirectMessageCreateResponse,
  DirectMessageRecipientOption,
  DirectMessagePost,
  DirectMessageThread,
  DirectMessageThreadDetail,
} from "../types";

import {
  serializeDirectMessageCreatePayload,
  serializeDirectMessageReplyPayload,
} from "./serializer";

export const DIRECT_MESSAGE_THREADS_PAGE_SIZE = 15;
export const DIRECT_MESSAGE_RECIPIENTS_LIMIT = 50;

function unwrapData<T>(response: AxiosResponse<{data: T}>): T {
  return response.data.data;
}

function unwrapListResponse<T>(response: AxiosResponse): GetListResponse<T> {
  const body = response.data as {
    data?: T[] | {data?: T[]};
    meta?: {total: number; from?: number; to?: number};
  };

  const items = Array.isArray(body.data)
    ? body.data
    : Array.isArray((body.data as {data?: T[]})?.data)
      ? (body.data as {data: T[]}).data
      : [];

  const meta = body.meta ?? {total: 0};

  return {
    data: items,
    meta: {
      total: meta.total,
      from: meta.from ?? 0,
      to: meta.to ?? 0,
    },
  };
}

export function formatParticipantLabel(participant: {
  name: string;
  role?: string | null;
  property_name?: string | null;
}): string {
  const role = participant.role?.trim();
  const property = participant.property_name?.trim();
  const parts = [participant.name.trim()];
  if (role) parts.push(role);
  if (property) parts.push(property);
  return parts.filter(Boolean).join(" · ");
}

class DirectMessagesService {
  async getThreads(params?: {
    box?: DirectMessageBox;
    unread?: boolean;
    page?: {limit: number; offset: number};
  }): Promise<GetListResponse<DirectMessageThread>> {
    const response = await directMessagesApi.getThreads({
      box: params?.box ?? "inbox",
      unread: params?.unread,
      page: params?.page ?? {
        limit: DIRECT_MESSAGE_THREADS_PAGE_SIZE,
        offset: 0,
      },
    });
    return unwrapListResponse<DirectMessageThread>(response);
  }

  /** Unified conversation list — merges inbox + sent for managers when not filtering unread. */
  async getConversationThreads(params: {
    mergeSent: boolean;
    unread?: boolean;
    page: number;
    limit: number;
  }): Promise<GetListResponse<DirectMessageThread>> {
    const {mergeSent, unread, page, limit} = params;

    if (unread) {
      return this.getThreads({
        box: "inbox",
        unread: true,
        page: {limit, offset: (page - 1) * limit},
      });
    }

    if (!mergeSent) {
      return this.getThreads({
        box: "inbox",
        page: {limit, offset: (page - 1) * limit},
      });
    }

    const fetchSize = page * limit;
    const [inbox, sent] = await Promise.all([
      this.getThreads({box: "inbox", page: {limit: fetchSize, offset: 0}}),
      this.getThreads({box: "sent", page: {limit: fetchSize, offset: 0}}),
    ]);

    const merged = mergeAndSortThreads([...inbox.data, ...sent.data]);
    const total = (inbox.meta?.total ?? 0) + (sent.meta?.total ?? 0);
    const data = sliceConversationPage(merged, page, limit);

    return {
      data,
      meta: {
        total,
        from: data.length > 0 ? (page - 1) * limit + 1 : 0,
        to: (page - 1) * limit + data.length,
      },
    };
  }

  async getRecipients(params?: {
    property_id?: number;
    recipient_type?: "staff" | "resident";
    search?: string;
    limit?: number;
  }): Promise<DirectMessageRecipientOption[]> {
    const response = await directMessagesApi.getRecipients({
      ...params,
      limit: params?.limit ?? DIRECT_MESSAGE_RECIPIENTS_LIMIT,
    });
    const list = unwrapListResponse<DirectMessageRecipientOption>(response);
    return list.data;
  }

  async getThread(id: number): Promise<DirectMessageThreadDetail> {
    const response = await directMessagesApi.getThread(id);
    return unwrapData<DirectMessageThreadDetail>(
      response as AxiosResponse<{data: DirectMessageThreadDetail}>,
    );
  }

  async createThread(
    values: Parameters<typeof serializeDirectMessageCreatePayload>[0],
  ): Promise<DirectMessageCreateResponse> {
    const payload = serializeDirectMessageCreatePayload(values);
    const response = await directMessagesApi.createThread(payload);
    return unwrapData<DirectMessageCreateResponse>(
      response as AxiosResponse<{data: DirectMessageCreateResponse}>,
    );
  }

  async replyToThread(threadId: number, body: string): Promise<DirectMessagePost> {
    const response = await directMessagesApi.createPost(
      threadId,
      serializeDirectMessageReplyPayload(body),
    );
    return unwrapData<DirectMessagePost>(
      response as AxiosResponse<{data: DirectMessagePost}>,
    );
  }

  async setRepliesEnabled(threadId: number, replies_enabled: boolean): Promise<unknown> {
    const response = await directMessagesApi.updateThread(threadId, {replies_enabled});
    return unwrapData(response);
  }

  getIsManagerOnThread(thread: DirectMessageThreadDetail["thread"], userId: number): boolean {
    return thread.created_by?.user_id === userId;
  }

  getCanReply(
    detail: DirectMessageThreadDetail,
    userId: number,
    canStore: boolean,
  ): boolean {
    const isManager = detail.thread.created_by?.user_id === userId;
    if (isManager) {
      return canStore;
    }
    if (!detail.thread.replies_enabled) {
      return false;
    }
    return detail.thread.recipient?.user_id === userId;
  }
}

export const directMessagesService = new DirectMessagesService();

export {serializeDirectMessageCreatePayload, serializeDirectMessageReplyPayload} from "./serializer";
